import dayjs from "dayjs";
import "dayjs/locale/pt-br";

import topImage from "@assets/img/banner_tegra_TI_1920x500.png";

import { ITrackingDTO, TrackingStatusEnum } from "@hooks/dtos/ITrackingDTO";
import {
  ITrackingHistDTO,
  TrackingHistStatusEnum,
} from "@hooks/dtos/ITrackingHistDTO";
import { Masks } from "@hooks/functions/Masks";
import {
  FaTruck,
  FaMapMarkerAlt,
  FaRegCopy,
  FaCalendarAlt,
  FaHome,
  FaCity,
  FaMailBulk,
  FaFlag,
  FaRoad,
  FaUser,
  FaFileInvoice,
  FaInfoCircle,
  FaWhatsapp,
} from "react-icons/fa";
import {
  TrackingHistConvertDeliveryStatus,
  TrackingHistConvertStatus,
} from "@hooks/functions/TrackingHist";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { TrackingProgressBar } from "./progress-bar";
import { useEffect, useState } from "react";
import { TrackingStatusMessage } from "./trackingStatus";
import { abbreviateName, formatName } from "@hooks/functions/formatName";
import { ZuAuthStore } from "@hooks/stores/zustand-auth-store";
import { LoadingSkeleton } from "@components/skeleton-loading";
import { config } from "config";

interface IProps {
  tracking: ITrackingDTO;
  trackingHist: ITrackingHistDTO[];
}

const masks = new Masks();
export const TrackingDetails = ({ tracking, trackingHist }: IProps) => {
  dayjs.locale("pt-br");

  const {
    actions: { pushToastify },
  } = ZuToastifyStore();
  const { customer } = ZuAuthStore();

  const invoiceSince = "2026-03-18";
  const [showInvoiceAlert, setShowInvoiceAlert] = useState<boolean>(false);

  const [isInSEFAZ, setIsInSEFAZ] = useState<boolean>(false);
  const [tryDelivery, setTryDelivery] = useState<boolean>(false);
  const [alreadyInSEFAZ, setAlreadyInSEFAZ] = useState<boolean>(false);
  const [historyIsLoading, setHistoryIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const lastTrackingHist =
      trackingHist.length > 0
        ? trackingHist[trackingHist.length - 1]
        : undefined;

    const havePendenciaEmbarque = trackingHist.some(
      (th) => th.status === TrackingHistStatusEnum.PENDENCIA_EMBARQUE,
    );
    if (!havePendenciaEmbarque) {
      // --- get the earliest date --- //
      const earliestDate = new Date(
        Math.min(
          ...trackingHist.map((item) => new Date(item.createdAt).getTime()),
        ),
      );

      // --- set the earliest date --- //
      const date =
        new Date(tracking.createdAt) < earliestDate
          ? tracking.createdAt
          : earliestDate;

      const tmpTrackHist = {
        id: 1,
        tracking: tracking.id,
        track: tracking.track,
        status: TrackingHistStatusEnum.PENDENCIA_EMBARQUE,
        createdAt: date,
      };
      trackingHist.unshift(tmpTrackHist);
    }

    if (lastTrackingHist) {
      setIsInSEFAZ(lastTrackingHist.status === TrackingHistStatusEnum.SEFAZ);
    }

    const alreadyInSeFaz = trackingHist.some(
      (th) => th.status === TrackingHistStatusEnum.SEFAZ,
    );
    setAlreadyInSEFAZ(alreadyInSeFaz);

    const hasDeliveredStatus = trackingHist.some(
      (hist) => hist.status === "te",
    );
    setTryDelivery(hasDeliveredStatus);

    // --- After treat the History --- //
    setHistoryIsLoading(false);
  }, [trackingHist]);

  useEffect(() => {
    if (
      customer &&
      tracking &&
      (tracking.customer === customer.customer.id ||
        tracking.salesperson === customer.customer.id)
    ) {
      const firstTrackHist =
        trackingHist.length > 0 ? trackingHist[0] : undefined;
      if (firstTrackHist) {
        const firstTrackHistDate = new Date(firstTrackHist.createdAt);
        // --- Create oldest Invoice alert --- //
        if (!tracking.invoice) {
          const since = new Date(invoiceSince);
          setShowInvoiceAlert(firstTrackHistDate < since);
        }

        // --- Fix the Tracking inicial date --- //
        if (firstTrackHistDate < new Date(tracking.createdAt)) {
          tracking.createdAt = firstTrackHistDate;
        }
      }
    }
  }, [customer]);

  // --- Load the Steps --- //
  const steps = TrackingHistConvertDeliveryStatus(tracking, trackingHist);

  // Build delivery address rows conditionally
  const deliveryRows = [
    tracking?.customerData?.name && {
      icon: <FaUser size={14} />,
      label: "Cliente",
      value: formatName(abbreviateName(tracking.customerData.name, 30)),
    },
    tracking.street && {
      icon: <FaRoad size={14} />,
      label: "Logradouro",
      value: tracking.street,
    },
    tracking.number && {
      icon: <FaRoad size={14} />,
      label: "Nº",
      value: tracking.number,
    },
    tracking.complement && {
      icon: <FaRoad size={14} />,
      label: "Complemento",
      value: tracking.complement,
    },
    tracking.hood && {
      icon: <FaMapMarkerAlt size={14} />,
      label: "Bairro",
      value: tracking.hood,
    },
    tracking.cityData?.name && {
      icon: <FaCity size={14} />,
      label: "Cidade / UF",
      value: `${tracking.cityData?.name} / ${tracking.stateData?.iso || `**`}`,
    },
    tracking.cep && {
      icon: <FaMailBulk size={14} />,
      label: "CEP",
      value: tracking.cep ? masks.postalCode(tracking.cep) : "Não Identificado",
    },
    tracking?.countryData && {
      icon: <FaFlag size={14} />,
      label: "País",
      value: tracking.countryData.name,
    },
  ].filter(Boolean) as {
    icon: React.ReactNode;
    label: string;
    value: string;
  }[];

  const isFinished = tracking.status === TrackingStatusEnum.FINISHED;

  return (
    <>
      {/* Banner Image - Full Width */}
      <div
        style={{
          marginLeft: "calc(-1 * clamp(1rem, 10vw, 20rem))",
          marginRight: "calc(-1 * clamp(1rem, 10vw, 20rem))",
          marginTop: "calc(-1 * clamp(2rem, 5vw, 5rem))",
          marginBottom: "2rem",
          height:
            window.innerWidth >= 768
              ? "clamp(180px, 28vh, 280px)"
              : "clamp(150px, 20vh, 200px)",
          overflow: "hidden",
          position: "relative",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div className="w-100 h-100 d-flex justify-content-center align-items-center px-0 px-md-5">
          <img
            src={topImage}
            alt=""
            className="d-block"
            style={{
              opacity: "0.8",
              width: "85%",
              height: "auto",
              objectFit: window.innerWidth >= 768 ? "cover" : "contain",
              objectPosition: "center",
            }}
          />
        </div>

        {/* Gradient overlay — desktop only */}
        <div
          className="d-none d-md-block"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "linear-gradient(90deg, #f5f7fd 9%, rgba(245,247,253,1) 10.5%, rgba(245,247,253,0.7) 12%, rgba(245,247,253,0.4) 13.5%, rgba(245,247,253,0) 15%, transparent 85%, rgba(245,247,253,0) 85%, rgba(245,247,253,0.4) 86.5%, rgba(245,247,253,0.7) 88%, rgba(245,247,253,1) 89.5%, #f5f7fd 91%)",
            pointerEvents: "none",
            zIndex: 2,
          }}
        />
      </div>

      <div className="container-fluid pt-0 mt-0">
        {/* Page title */}
        <div className="row mb-4">
          <div className="col-12 text-center">
            <h2 className="fw-bold">Acompanhe seu pedido</h2>
          </div>
        </div>

        {/* Progress Bar */}
        <TrackingProgressBar steps={steps} />

        {/* Summary Cards: Order Number | Tracking Code | Status */}
        <div className="row mb-4 g-3">
          {/* Order number */}
          <div className="col-md-4">
            <div
              className="card h-100"
              style={{ border: "2px solid #5fc4d9", borderRadius: "12px" }}
            >
              <div className="card-body d-flex flex-column text-center py-4">
                <div className="text-muted mb-2" style={{ fontSize: "14px" }}>
                  Número do Pedido
                </div>
                <div className="flex-grow-1 d-flex align-items-center justify-content-center">
                  <div
                    className="fw-bold d-flex align-items-center justify-content-center gap-2"
                    style={{ fontSize: "20px" }}
                  >
                    {tracking.code}
                    <button
                      className="btn btn-sm btn-link p-0"
                      onClick={() => {
                        navigator.clipboard.writeText(String(tracking.code));
                        pushToastify({
                          message: "Nº do Pedido copiado com sucesso!",
                          type: "info",
                          theme: "dark",
                        });
                      }}
                      style={{ color: "#5fc4d9" }}
                    >
                      <FaRegCopy className="fs-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tracking code */}
          <div className="col-md-4">
            <div
              className="card h-100"
              style={{ border: "2px solid #5fc4d9", borderRadius: "12px" }}
            >
              <div className="card-body d-flex flex-column text-center py-4">
                <div className="text-muted mb-2" style={{ fontSize: "14px" }}>
                  Código de Rastreio
                </div>
                <div className="flex-grow-1 d-flex align-items-center justify-content-center">
                  <div
                    className="fw-bold d-flex align-items-center justify-content-center gap-2"
                    style={{ fontSize: "20px" }}
                  >
                    {tracking.companyTrackCode ? (
                      <>
                        {tracking.companyTrackCode.toLocaleUpperCase()}
                        <button
                          className="btn btn-sm btn-link p-0"
                          onClick={() => {
                            navigator.clipboard.writeText(
                              (
                                tracking.companyTrackCode || "~~"
                              ).toLocaleUpperCase(),
                            );
                            pushToastify({
                              message: "Código copiado com sucesso!",
                              type: "info",
                              theme: "dark",
                            });
                          }}
                          style={{ color: "#5fc4d9" }}
                        >
                          <FaRegCopy className="fs-5" />
                        </button>
                      </>
                    ) : (
                      <span className="fs-5">Em andamento...</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Forecast / Status */}
          <div className="col-md-4">
            <div
              className="card h-100"
              style={{ border: "2px solid #5fc4d9", borderRadius: "12px" }}
            >
              <div className="card-body d-flex flex-column text-center py-4">
                <TrackingStatusMessage
                  tracking={tracking}
                  isInSEFAZ={isInSEFAZ}
                  alreadyInSEFAZ={alreadyInSEFAZ}
                  tryDelivery={tryDelivery}
                />
              </div>
            </div>
          </div>
        </div>

        {/* ============================================================
            Compact Inline Delivery Info — Variant 1 (fixed)
        ============================================================ */}
        <div className="row mb-4">
          <div className="col-12">
            <div
              className="d-flex align-items-center flex-wrap px-4 py-3"
              style={{
                gap: "20px",
                borderRadius: "16px",
                border: "2px solid #5fc4d9",
                backgroundColor: "#ffffff",
                boxShadow: "0 4px 24px rgba(95,196,217,0.15)",
              }}
            >
              {/* Icon + Title */}
              <div className="d-flex align-items-center gap-2 flex-shrink-0">
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle"
                  style={{
                    width: "42px",
                    height: "42px",
                    backgroundColor: "#e0f7fa",
                    color: "#5fc4d9",
                    flexShrink: 0,
                  }}
                >
                  <FaMapMarkerAlt size={18} />
                </div>
                <span
                  className="text-uppercase fw-bold"
                  style={{
                    color: "#5fc4d9",
                    fontSize: "0.8rem",
                    letterSpacing: "0.1em",
                  }}
                >
                  Informações de Entrega
                </span>
              </div>

              {/* Vertical Divisor */}
              <div
                className="d-none d-md-block flex-shrink-0"
                style={{
                  width: "1px",
                  height: "40px",
                  backgroundColor: "#d0eef3",
                }}
              />

              {/* Inline Fields */}
              <div
                className="d-flex flex-wrap flex-grow-1"
                style={{ gap: "8px 32px" }}
              >
                {deliveryRows.map((row) => (
                  <div key={row.label} className="d-flex flex-column">
                    <span
                      className="text-uppercase fw-semibold"
                      style={{
                        fontSize: "0.9rem",
                        color: "#5fc4d9",
                        letterSpacing: "0.1em",
                        marginBottom: "3px",
                      }}
                    >
                      {row.label}
                    </span>
                    <span
                      className="fw-semibold"
                      style={{ fontSize: "1rem", color: "#1e293b" }}
                    >
                      {row.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ============================================================
            Compact Inline Delivery Info — Variant 1 (fixed)
        ============================================================ */}
        {/* Invoice Alert */}
        {showInvoiceAlert && (
          <div
            className="alert mb-4"
            style={{
              backgroundColor: "rgba(255, 140, 0, 0.27)",
              border: "1px solid rgba(255, 140, 0, 0.3)",
              borderRadius: "12px",
            }}
          >
            <div className="d-flex align-items-center justify-content-center">
              <FaInfoCircle className="fs-5 me-2" />
              Pedidos realizados antes de{" "}
              {dayjs(invoiceSince).format("DD [de] MMMM [de] YYYY")} não tem
              Proform Invoices disponíveis nesta plataforma.
            </div>
          </div>
        )}

        {/* Invoice Button */}
        {tracking.invoice && (
          <div className="row mb-4">
            <div className="col-12">
              <button
                className="btn w-100 text-white fw-semibold py-3"
                style={{
                  backgroundColor: "#5fc4d9",
                  borderRadius: "8px",
                  border: "none",
                }}
                onClick={() => {
                  window.open(tracking.invoice, "_blank");
                }}
              >
                <FaFileInvoice className="fs-4 me-2" />
                Visualizar a Proform Invoice do Pedido
              </button>
            </div>
          </div>
        )}

        {/* ============================================================
            Classic layout: Carrier | Deadlines (original style)
        ============================================================ */}
        <div className="row mb-5 g-4">
          {/* Carrier */}
          <div className="col-md-6">
            <div className="d-flex gap-3">
              <div
                className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                style={{
                  width: "56px",
                  height: "56px",
                  backgroundColor: "#5fc4d9",
                  color: "white",
                }}
              >
                <FaTruck size={24} />
              </div>
              <div>
                <h5 className="mb-2" style={{ color: "#5fc4d9" }}>
                  Dados da Transportadora
                </h5>
                <div style={{ fontSize: "14px" }}>
                  <div className="mb-1">
                    <strong>Transportadora:</strong>{" "}
                    {(tracking.company || "~~").toLocaleUpperCase()}
                  </div>
                  <div>
                    <strong>Método de Envio:</strong>{" "}
                    {(tracking.shippingMethod || "~~").toLocaleUpperCase()}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Deadlines / delivery confirmation */}
          <div className="col-md-6">
            <div className="d-flex gap-3">
              <div
                className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                style={{
                  width: "56px",
                  height: "56px",
                  backgroundColor: "#5fc4d9",
                  color: "white",
                }}
              >
                {isFinished ? (
                  <FaHome size={24} />
                ) : (
                  <FaCalendarAlt size={24} />
                )}
              </div>
              <div>
                <h5 className="mb-2" style={{ color: "#5fc4d9" }}>
                  {isFinished ? "Entrega Efetivada" : "Informações de Prazos"}
                </h5>
                <div style={{ fontSize: "14px", paddingInlineEnd: "2rem" }}>
                  <div className="mb-1">
                    {isFinished
                      ? "A entrega foi concluída com sucesso."
                      : "A previsão pode variar em casos de análises por Órgãos Responsáveis"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Detailed History Timeline */}
        <div className="row">
          <div className="col-12">
            <h3 className="fw-bold mb-4">Histórico Detalhado</h3>

            <div className="position-relative ps-4">
              {/* Vertical timeline line */}
              <div
                className="position-absolute"
                style={{
                  left: "8px",
                  top: "8px",
                  bottom: "8px",
                  width: "2px",
                  backgroundColor: "#e0e0e0",
                }}
              />

              {/* History events */}
              {historyIsLoading ? (
                <LoadingSkeleton />
              ) : (
                trackingHist.map((hist, index) => {
                  const status = TrackingHistConvertStatus(hist.status);
                  return (
                    <div key={index} className="position-relative mb-4 ps-3">
                      {/* Timeline dot */}
                      <div
                        className="position-absolute rounded-circle"
                        style={{
                          left: "-4px",
                          top: "4px",
                          width: "12px",
                          height: "12px",
                          backgroundColor: status.color,
                        }}
                      />

                      {/* Event content */}
                      <div>
                        <div
                          className="fw-semibold mb-1"
                          style={{ fontSize: "15px" }}
                        >
                          {status.text} —{" "}
                          {dayjs(hist.createdAt).format(
                            "dddd, DD [de] MMMM [de] YYYY",
                          )}
                          .
                        </div>
                        <div
                          className="text-muted"
                          style={{ fontSize: "14px" }}
                        >
                          {status.description}

                          {/* Button if - * Pendencia de Validação * - */}
                          {hist.status ===
                            TrackingHistStatusEnum.PENDENCIA_VALIDACAO &&
                            (() => {
                              const message = `Olá, preciso de ajuda com meu pedido.
                                Status atual: ${status.text}
                                Número do pedido: ${tracking.code}  
                                Data: ${dayjs(tracking.createdAt).format("DD/MM/YYYY")}
                                Poderia me auxiliar?`;

                              return (
                                <a
                                  href={`https://wa.me/55${config.contact.whatsapp}?text=${encodeURIComponent(message)}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-outline-primary py-0 px-2 ms-2"
                                  aria-label="WhatsApp"
                                >
                                  <FaWhatsapp
                                    className="text-success me-1"
                                    size={24}
                                  />
                                  <span className="text-muted">
                                    Atendimento
                                  </span>
                                </a>
                              );
                            })()}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
